import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import {
    type ProductMetricsDto,
    getReportSavingsLastRequestParams,
} from 'entities/ReportSavingsSummary';
import { $api } from 'shared/api/api';
import { useSavingsSummaryReportVisible } from 'shared/lib/hooks/useSavingsSummaryReportVisible';
import { formatReportPercent } from 'shared/lib/reportFormatters';

interface ProductsSkuRequestBody {
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
    orderBy?: string;
    page?: number;
    pageSize?: number;
}

interface ProductOrderSummaryItem {
    submittedAt: string;
    order: {
        orderId: string;
        orderNumber: string;
    };
    propertyName: string;
    suppliers: string[];
    quantity: number;
    totalPaid: number;
    baselinePrice: number;
    totalSaved: number;
    percentSaved: number;
    totalMissed?: number;
    missedPercent?: number;
    totalOverspend?: number;
    overspendPercent?: number;
}

interface ProductOrdersBySkuResponse {
    count: number;
    page: number;
    totalPages: number;
    items: ProductOrderSummaryItem[];
}

interface UseReportsPageSavingsSummaryProductResult {
    isSavingsSummaryVisible: boolean | undefined;
    sku: string | undefined;
    product: ProductMetricsDto | null;
    fromMissedSavings: boolean;
    loading: boolean;
    error: string | null;
    ordersData: ProductOrdersBySkuResponse | null;
    dateFrom: Date;
    dateTo: Date;
    datePreset?: string;
    totalSaved: number;
    totalSpent: number;
    baselineTotal: number;
    totalQuantity: number;
    ordersCount: number;
    savingsPercentLabel: string;
    avgSupplierPricePerUnit: number | null;
    lastRequestParamsPresent: boolean;
    canLoadMoreOrders: boolean;
    loadMoreOrders: () => void;
}

export const useReportsPageSavingsSummaryProduct =
    (): UseReportsPageSavingsSummaryProductResult => {
        const { sku } = useParams<{ sku: string }>();
        const location = useLocation();
        const isSavingsSummaryVisible = useSavingsSummaryReportVisible();
        const lastRequestParams = useSelector(
            getReportSavingsLastRequestParams,
        );

        const locationState = location.state as {
            product?: ProductMetricsDto;
            fromMissedSavings?: boolean;
        } | null;
        const product = locationState?.product ?? null;
        const fromMissedSavings = locationState?.fromMissedSavings === true;

        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [ordersData, setOrdersData] =
            useState<ProductOrdersBySkuResponse | null>(null);
        const activeRequestControllerRef = useRef<AbortController | null>(null);

        const loadOrders = useCallback(
            async (page: number) => {
                if (!sku || !lastRequestParams) {
                    return;
                }

                activeRequestControllerRef.current?.abort();
                const controller = new AbortController();
                activeRequestControllerRef.current = controller;

                setLoading(true);
                setError(null);

                try {
                    const requestBody: ProductsSkuRequestBody = {
                        dateFrom: lastRequestParams.dateFrom,
                        dateTo: lastRequestParams.dateTo,
                        propertyIds: lastRequestParams.propertyIds,
                        categoryIds: lastRequestParams.categoryIds,
                        orderBy: 'submitted_at',
                        page,
                        pageSize: 25,
                    };

                    const response =
                        await $api.post<ProductOrdersBySkuResponse>(
                            `/v1/reports/savings-insights/products/${encodeURIComponent(
                                sku,
                            )}`,
                            requestBody,
                            { signal: controller.signal },
                        );

                    setOrdersData((prev) => {
                        if (!prev || page === 1) {
                            return response.data;
                        }

                        return {
                            ...response.data,
                            items: [...prev.items, ...response.data.items],
                        };
                    });
                } catch (error) {
                    if (axios.isCancel(error)) {
                        return;
                    }
                    setError('failed');
                } finally {
                    if (activeRequestControllerRef.current === controller) {
                        activeRequestControllerRef.current = null;
                        setLoading(false);
                    }
                }
            },
            [lastRequestParams, sku],
        );

        useEffect(() => {
            const wrapped = async () => {
                await loadOrders(1);
            };

            wrapped();

            return () => {
                activeRequestControllerRef.current?.abort();
                activeRequestControllerRef.current = null;
            };
        }, [loadOrders]);

        const canLoadMoreOrders =
            Boolean(ordersData) &&
            ordersData!.page < ordersData!.totalPages &&
            !loading;

        const loadMoreOrders = useCallback((): void => {
            if (!ordersData || ordersData.page >= ordersData.totalPages) {
                return;
            }
            loadOrders(ordersData.page + 1).catch((): void => {});
        }, [loadOrders, ordersData]);

        const dateFrom = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateFrom)
                    : new Date(),
            [lastRequestParams],
        );
        const dateTo = useMemo(
            () =>
                lastRequestParams
                    ? new Date(lastRequestParams.dateTo)
                    : new Date(),
            [lastRequestParams],
        );

        const {
            totalSaved,
            totalSpent,
            baselineTotal,
            totalQuantity,
            ordersCount,
            savingsPercentLabel,
            avgSupplierPricePerUnit,
        } = useMemo(() => {
            const totalSavedNum =
                product?.totalSaved == null ? 0 : Number(product.totalSaved);
            const totalSpentNum =
                product?.totalPaid == null ? 0 : Number(product.totalPaid);
            const baseline = totalSavedNum + totalSpentNum;
            const quantity = product?.totalQty ?? 0;
            const orders = ordersData?.count ?? 0;
            const savingsPctLabel = product
                ? formatReportPercent(product.percentSaved ?? '0')
                : '';
            const avgPerUnit = quantity > 0 ? baseline / quantity : null;

            return {
                totalSaved: totalSavedNum,
                totalSpent: totalSpentNum,
                baselineTotal: baseline,
                totalQuantity: quantity,
                ordersCount: orders,
                savingsPercentLabel: savingsPctLabel,
                avgSupplierPricePerUnit: avgPerUnit,
            };
        }, [ordersData, product]);

        return {
            isSavingsSummaryVisible,
            sku,
            product,
            fromMissedSavings,
            loading,
            error,
            ordersData,
            dateFrom,
            dateTo,
            datePreset: lastRequestParams?.datePreset,
            totalSaved,
            totalSpent,
            baselineTotal,
            totalQuantity,
            ordersCount,
            savingsPercentLabel,
            avgSupplierPricePerUnit,
            lastRequestParamsPresent: Boolean(lastRequestParams),
            canLoadMoreOrders,
            loadMoreOrders,
        };
    };
